import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Order, PaymentMethod } from '@/types';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle, 
  CreditCard, 
  DollarSign, 
  Wallet, 
  Banknote, 
  QrCode, 
  Plus, 
  Trash2,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { InputNumber } from '@/components/ui/input-number';
import { formatCurrency } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { clearTableData } from '@/services/orderApiService';

interface PaymentConfirmationDialogProps {
  order?: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (order: Order | undefined, paymentMethods: PaymentMethod[], paymentValues: number[]) => void;
  totalAmount?: number;
  isProcessing?: boolean;
  title?: string;
  description?: string;
  defaultPeopleCount?: number;
}

interface PaymentEntry {
  method: PaymentMethod;
  amount: number;
  personIndex?: number;
}

const toCents = (value: number) => {
  if (!Number.isFinite(value)) return 0;
  return Math.round((value + Number.EPSILON) * 100);
};

const fromCents = (cents: number) => {
  if (!Number.isFinite(cents)) return 0;
  return cents / 100;
};

const PaymentConfirmationDialog: React.FC<PaymentConfirmationDialogProps> = ({
  order,
  open,
  onOpenChange,
  onConfirm,
  totalAmount: propTotalAmount,
  isProcessing = false,
  title = "Confirmar Pagamento",
  description = "Adicione os métodos de pagamento para finalizar o pedido",
  defaultPeopleCount
}) => {
  const { paymentMethods } = useApp();
  const { toast } = useToast();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [paymentEntries, setPaymentEntries] = useState<PaymentEntry[]>([]);
  const [currentAmount, setCurrentAmount] = useState<string>('');
  const [remainingAmount, setRemainingAmount] = useState<number>(0);
  const [overpaidAmount, setOverpaidAmount] = useState<number>(0);
  
  const [splitBillEnabled, setSplitBillEnabled] = useState(false);
  const [peopleCount, setPeopleCount] = useState<number>(2);
  const [selectedPersonIndex, setSelectedPersonIndex] = useState<number>(0);

  const isMobile = useIsMobile();
  
  const effectiveTotalAmount = propTotalAmount !== undefined ? propTotalAmount : (order?.totalAmount || 0);
  
  const perPersonAmount = useMemo(() => {
    const count = Math.max(1, Number.isFinite(peopleCount) ? peopleCount : 1);
    return effectiveTotalAmount / count;
  }, [effectiveTotalAmount, peopleCount]);
  
  const paidByPerson = useMemo(() => {
    const result = Array.from({ length: Math.max(1, peopleCount) }, () => 0);
    for (const entry of paymentEntries) {
      if (entry.personIndex == null) continue;
      if (entry.personIndex >= 0 && entry.personIndex < result.length) {
        result[entry.personIndex] += entry.amount;
      }
    }
    return result;
  }, [paymentEntries, peopleCount]);
  
  const remainingForSelectedPerson = useMemo(() => {
    if (!splitBillEnabled) return remainingAmount;
    const idx = Math.min(Math.max(0, selectedPersonIndex), Math.max(0, peopleCount - 1));
    const remaining = perPersonAmount - (paidByPerson[idx] ?? 0);
    return remaining > 0 ? remaining : 0;
  }, [splitBillEnabled, remainingAmount, selectedPersonIndex, perPersonAmount, paidByPerson, peopleCount]);

  useEffect(() => {
    if (open) {
      setPaymentEntries([]);
      setSelectedPaymentMethod(null);
      setCurrentAmount('');
      setRemainingAmount(effectiveTotalAmount);
      setOverpaidAmount(0);
      
      setSplitBillEnabled(false);
      const fromProp = Number(defaultPeopleCount);
      const fromOrder = Number((order as any)?.tableSeats ?? (order as any)?.seats ?? (order as any)?.table?.seats);
      const candidate = Number.isFinite(fromProp) && fromProp > 0 ? fromProp : (Number.isFinite(fromOrder) && fromOrder > 0 ? fromOrder : 2);
      const defaultPeople = Math.max(1, Math.min(20, candidate));
      setPeopleCount(defaultPeople);
      setSelectedPersonIndex(0);
    }
  }, [open, effectiveTotalAmount, defaultPeopleCount, order]);
  
  useEffect(() => {
    const totalPaidCents = paymentEntries.reduce((sum, entry) => sum + toCents(entry.amount), 0);
    const totalCents = toCents(effectiveTotalAmount);
    const remainingCents = totalCents - totalPaidCents;

    setRemainingAmount(fromCents(Math.max(0, remainingCents)));
    setOverpaidAmount(fromCents(Math.max(0, -remainingCents)));
  }, [paymentEntries, effectiveTotalAmount]);
  
  useEffect(() => {
    if (selectedPersonIndex >= peopleCount) {
      setSelectedPersonIndex(Math.max(0, peopleCount - 1));
    }
  }, [peopleCount, selectedPersonIndex]);

  const handleAddPayment = () => {
    if (!selectedPaymentMethod) {
      toast({
        title: "Selecione um método de pagamento",
        description: "Por favor, selecione um método de pagamento antes de adicionar.",
        variant: "destructive"
      });
      return;
    }
    
    const amountValue = parseFloat(currentAmount);
    if (isNaN(amountValue) || amountValue <= 0) {
      toast({
        title: "Valor inválido",
        description: "Por favor, informe um valor válido para o pagamento.",
        variant: "destructive"
      });
      return;
    }
    
    setPaymentEntries([...paymentEntries, {
      method: selectedPaymentMethod,
      amount: amountValue,
      personIndex: splitBillEnabled ? selectedPersonIndex : undefined
    }]);
    
    setCurrentAmount('');
    setSelectedPaymentMethod(null);
  };
  
  const handleRemovePayment = (index: number) => {
    const updatedEntries = [...paymentEntries];
    updatedEntries.splice(index, 1);
    setPaymentEntries(updatedEntries);
  };
  
  const handleConfirm = async () => {
    if (paymentEntries.length === 0) {
      toast({
        title: "Nenhum pagamento informado",
        description: "Por favor, adicione pelo menos um método de pagamento.",
        variant: "destructive"
      });
      return;
    }

    const totalPaidCents = paymentEntries.reduce((sum, entry) => sum + toCents(entry.amount), 0);
    const totalCents = toCents(effectiveTotalAmount);

    if (totalPaidCents < totalCents) {
      const totalPaid = fromCents(totalPaidCents);
      toast({
        title: "Valor insuficiente",
        description: `O valor total pago (${formatCurrency(totalPaid)}) é menor que o valor da venda (${formatCurrency(effectiveTotalAmount)}).`,
        variant: "destructive"
      });
      return;
    }
    
    const methods = paymentEntries.map(entry => entry.method);
    const values = paymentEntries.map(entry => entry.amount);
    
    const changeCents = Math.max(0, totalPaidCents - totalCents);
    const changeAmount = fromCents(changeCents);
    const receivedAmount = fromCents(totalPaidCents);
    
    let updatedOrder = order;
      if (order) {
          updatedOrder = {
              ...order,
              receivedAmount,
              changeAmount: changeAmount,
              paymentDate: new Date(),
              status: 'paid'  // Ensure the order is marked as paid
          };

          // Clear table data after successful payment
          if (updatedOrder.tableId) {
              try {
                  await Promise.resolve(clearTableData(updatedOrder.tableId));
              } catch (err) {
                  console.warn('Falha ao limpar dados da mesa após pagamento:', err);
              }
          }
      }

    try {
      // Call the onConfirm callback with the payment information
      await Promise.resolve(onConfirm(updatedOrder, methods, values));
      // Close the dialog
      onOpenChange(false);
    } catch (error) {
      console.error('Erro ao confirmar pagamento:', error);
      toast({
        title: 'Erro ao confirmar pagamento',
        description: 'Ocorreu um erro ao confirmar o pagamento. Tente novamente.',
        variant: 'destructive'
      });
    }
   };
  
  const getPaymentIcon = (type: string) => {
    switch (type) {
      case 'cash':
        return <Banknote className="h-5 w-5" />;
      case 'credit':
        return <CreditCard className="h-5 w-5" />;
      case 'debit':
        return <Wallet className="h-5 w-5" />;
      case 'pix':
        return <QrCode className="h-5 w-5" />;
      default:
        return <DollarSign className="h-5 w-5" />;
    }
  };
  
  const suggestedAmounts = [
    50, 100, remainingAmount
  ].filter(amount => amount > 0);
  
  const effectiveSuggestedRemaining = splitBillEnabled ? remainingForSelectedPerson : remainingAmount;
  const suggestedAmountsEffective = [50, 100, effectiveSuggestedRemaining].filter(amount => amount > 0);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent 
          className={`flex flex-col ${isMobile ? "w-[90vw] max-w-[90vw] p-4" : ""}`}
          style={{ maxHeight: '90vh' }}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 flex-1 min-h-0 overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center py-2 border-b">
                <div className="text-sm text-muted-foreground">Valor Total</div>
                <div className="text-xl font-bold">
                  {formatCurrency(effectiveTotalAmount)}
                </div>
              </div>
              <div className="text-center py-2 border-b">
                <div className="text-sm text-muted-foreground">Falta Pagar</div>
                <div className={`text-xl font-bold ${remainingAmount > 0 ? 'text-red-500' : 'text-green-500'}`}>
                  {remainingAmount > 0 
                    ? formatCurrency(remainingAmount) 
                    : 'Pago'}
                </div>
              </div>
            </div>
            
            <div className="border rounded-md p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="space-y-0.5">
                  <Label htmlFor="split-bill">Dividir conta</Label>
                  <div className="text-xs text-muted-foreground">
                    Rateia o valor pela quantidade de pessoas e permite lançar pagamentos por pessoa.
                  </div>
                </div>
                <Switch
                  id="split-bill"
                  checked={splitBillEnabled}
                  onCheckedChange={(checked) => {
                    setSplitBillEnabled(checked);
                    setSelectedPersonIndex(0);
                  }}
                />
              </div>
              
              {splitBillEnabled && (
                <div className="mt-3 grid gap-3">
                  <div className="flex items-center gap-2">
                    <Label className="w-28">Pessoas</Label>
                    <div className="w-28">
                      <InputNumber
                        min={1}
                        max={20}
                        value={peopleCount}
                        onChange={(value) => {
                          const next = Math.max(1, Math.min(20, Number(value || 1)));
                          setPeopleCount(Number.isFinite(next) ? next : 1);
                        }}
                      />
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Por pessoa: <span className="font-medium">{formatCurrency(perPersonAmount)}</span>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {Array.from({ length: peopleCount }).map((_, idx) => {
                      const remaining = Math.max(0, perPersonAmount - (paidByPerson[idx] ?? 0));
                      const isSelected = idx === selectedPersonIndex;
                      return (
                        <Button
                          key={idx}
                          type="button"
                          variant={isSelected ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setSelectedPersonIndex(idx)}
                        >
                          Pessoa {idx + 1} • {formatCurrency(remaining)}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            
            {paymentEntries.length > 0 && (
              <div className="border rounded-md p-3 bg-muted/30">
                <h3 className="font-medium text-sm mb-2">Formas de Pagamento Adicionadas</h3>
                <div className="space-y-2">
                  {paymentEntries.map((entry, index) => (
                    <div key={index} className="flex items-center justify-between border-b pb-2">
                      <div className="flex items-center gap-2">
                        <div className={`p-1 rounded-full bg-primary text-white`}>
                          {getPaymentIcon(entry.method.type || 'other')}
                        </div>
                        <div>
                          <div className="text-sm font-medium">{entry.method.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {formatCurrency(entry.amount)}
                            {splitBillEnabled && entry.personIndex != null ? ` • Pessoa ${entry.personIndex + 1}` : ''}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemovePayment(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                
                {overpaidAmount > 0 && (
                  <div className="mt-2 p-2 bg-green-100 rounded-md flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-700">Troco: {formatCurrency(overpaidAmount)}</span>
                  </div>
                )}
              </div>
            )}
            
            <div className="border rounded-md p-3">
              <h3 className="font-medium text-sm mb-2">Adicionar Forma de Pagamento</h3>
              
              <div className="space-y-3">
                <ScrollArea className="h-24 pr-4">
                  <div className="grid grid-cols-2 gap-2">
                    {paymentMethods.map(method => (
                      <div
                        key={method.id}
                        className={`p-2 border rounded-lg cursor-pointer transition-all ${
                          selectedPaymentMethod?.id === method.id 
                            ? 'border-primary bg-primary/5' 
                            : 'hover:border-gray-400'
                        }`}
                        onClick={() => setSelectedPaymentMethod(method)}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`p-1 rounded-full ${
                            selectedPaymentMethod?.id === method.id 
                              ? 'bg-primary text-white' 
                              : 'bg-muted'
                          }`}>
                            {getPaymentIcon(method.type || 'other')}
                          </div>
                          <div className="text-sm">{method.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                
                <div className="grid gap-2">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      placeholder="Valor"
                      value={currentAmount}
                      onChange={(e) => setCurrentAmount(e.target.value)}
                      className="flex-1"
                    />
                    <Button 
                      variant="outline" 
                      type="button"
                      disabled={!selectedPaymentMethod || remainingAmount <= 0}
                      onClick={() => setCurrentAmount((splitBillEnabled ? remainingForSelectedPerson : remainingAmount).toFixed(2).toString())}
                    >
                      Faltante
                    </Button>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {suggestedAmountsEffective.map((amount, index) => (
                      <Button 
                        key={index}
                        variant="outline" 
                        size="sm"
                        type="button" 
                        onClick={() => setCurrentAmount(amount.toFixed(2).toString())}
                        disabled={!selectedPaymentMethod}
                      >
                        {formatCurrency(amount)}
                      </Button>
                    ))}
                  </div>
                  
                  <Button
                    onClick={handleAddPayment}
                    disabled={!selectedPaymentMethod || !currentAmount}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Pagamento
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="mt-4 pt-2 border-t shrink-0">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isProcessing}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={paymentEntries.length === 0 || remainingAmount > 0 || isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                <>Confirmar Pagamento</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentConfirmationDialog;
